/**
 * Coordinates, markers and the route line — everything the map DRAWS.
 *
 * **The order of a pair is the bug this module exists to prevent.** The backend
 * speaks `[lat, lon]` (`graphs/peterhof_canvas/components.py`, `Route.points`,
 * and `Pin.lat`/`Pin.lon`); MapGL speaks `[longitude, latitude]` everywhere,
 * without exception. Swapping them puts Peterhof off the coast of Somalia — far
 * enough to catch by eye, and quiet enough to reach a review. So there is ONE
 * converter, it is `toLngLat`, and it is covered by a test. No literal pair is
 * built anywhere else in `src/map` or `src/nav`.
 *
 * The second fact shaping this file comes from the SDK's own types: `Marker`
 * and `HtmlMarker` can be updated in place, `Polyline` can only be
 * `destroy()`ed. That is why markers are diffed by id and the route is
 * rebuilt — and why the route is TWO lines (walked, ahead) rather than one
 * recoloured along its length: a moving cut point then rebuilds the two short
 * lines instead of the whole walk on every GPS tick.
 */

import type * as MapGL from '@2gis/mapgl/types';
import type { HtmlMarker, LngLatBounds, Map as MapGLMap, Polyline } from '@2gis/mapgl/types';

import { cssToken } from './theme';
import { stripEmoji } from '../lib/emoji';

export type LngLat = [number, number];

/** The board's own pin shape, as `MapCardProps['pins'][number]` gives it. */
export type PinModel = {
  id: string;
  label: string;
  lat: number;
  lon: number;
  kind: string;
  n?: number | undefined;
  /** The board element this pin stands for, when it has one. */
  ref?: string | undefined;
};

/** The one conversion. Longitude first — that is MapGL's whole convention. */
export function toLngLat(point: { lat: number; lon: number }): LngLat {
  return [point.lon, point.lat];
}

/**
 * `route.points` as the wire has it: a list of `[lat, lon]` pairs.
 *
 * Anything shorter than a pair, or carrying a value that is not finite, is
 * dropped rather than drawn at `[0, 0]` — a route with one broken vertex is
 * still a usable route, and a line running through the Gulf of Guinea is not.
 */
export function pointsToLngLat(points: readonly (readonly number[])[] | undefined): LngLat[] {
  if (!points) return [];
  const out: LngLat[] = [];
  for (const pair of points) {
    if (!Array.isArray(pair) || pair.length < 2) continue;
    const lat = pair[0];
    const lon = pair[1];
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    out.push([lon as number, lat as number]);
  }
  return out;
}

/** The box around a set of points, or `null` when there are none. */
export function boundsOf(points: readonly LngLat[]): LngLatBounds | null {
  if (points.length === 0) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const [lng, lat] of points) {
    if (lng < west) west = lng;
    if (lng > east) east = lng;
    if (lat < south) south = lat;
    if (lat > north) north = lat;
  }
  return { southWest: [west, south], northEast: [east, north] };
}

/** Escapes text going into an `HtmlMarker`'s `html`. A label is museum copy,
 *  but it arrives over the wire and the marker is innerHTML either way. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * The pin's own markup.
 *
 * `HtmlMarker` rather than `Marker`: a `Marker` with no `icon` wears the SDK's
 * default teardrop, which belongs to 2GIS's design system and not to this
 * board, and dressing it would mean shipping icon PNGs. The pill below is
 * styled from `tokens.css` (`.map-pin` in `board.css`), so it is the same navy
 * as the rest of the card and follows the theme for free.
 */
export function pinHtml(pin: PinModel): string {
  const number = typeof pin.n === 'number' ? String(pin.n) : '';
  const kind = /^[a-z]+$/.test(pin.kind) ? pin.kind : 'sight';
  return (
    `<span class="map-pin map-pin--${kind}" title="${escapeHtml(stripEmoji(pin.label))}">` +
    `${escapeHtml(number)}</span>`
  );
}

/**
 * `live` is a MUTABLE holder and that is the whole point of it.
 *
 * A tappable marker's click listener is bound ONCE, at construction, and the
 * marker is then reused across board frames. Closing over the `pin` of the call
 * that built it meant a pin whose `ref` later changed went on scrolling to the
 * card it named on the first frame — the visitor sent to the wrong card, or to
 * one that had since left the board. The listener reads this holder instead, and
 * every sync writes the current pin into it, so the marker survives and the
 * answer is always this frame's.
 */
type MarkerEntry = {
  marker: HtmlMarker;
  at: LngLat;
  html: string;
  tappable: boolean;
  live: { pin: PinModel; onTap?: ((pin: PinModel) => void) | undefined };
};
export type MarkerBag = Map<string, MarkerEntry>;

/** `[lng, lat]` pairs compared for equality without allocating a string. */
function samePoint(a: LngLat, b: LngLat): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

/**
 * Brings the markers on the map in line with `pins`, by id.
 *
 * A pin whose id is already on the map is MOVED and re-labelled rather than
 * destroyed and rebuilt: recreating a marker is a visible blink, and the board
 * updates a map card on every frame of a turn.
 *
 * Returns the new bag; the caller keeps it in a ref.
 */
export function syncMarkers(
  map: MapGLMap,
  sdk: Pick<typeof MapGL, 'HtmlMarker'>,
  bag: MarkerBag,
  pins: readonly PinModel[],
  onTap?: (pin: PinModel) => void,
): MarkerBag {
  const next: MarkerBag = new Map();
  for (const pin of pins) {
    const at = toLngLat(pin);
    const html = pinHtml(pin);
    // Only a pin that STANDS FOR a card captures the pointer. Everywhere else
    // the pin must let a drag through, or the map develops 28-pixel dead spots
    // exactly where the visitor wants to grab it.
    const tappable = Boolean(pin.ref && onTap);
    const existing = bag.get(pin.id);
    if (existing && existing.tappable === tappable) {
      if (!samePoint(existing.at, at)) existing.marker.setCoordinates(at);
      if (existing.html !== html) existing.marker.setContent(html);
      // The listener reads this object, so a changed `ref` — or a changed
      // handler — takes effect without rebuilding the marker and blinking.
      existing.live.pin = pin;
      existing.live.onTap = onTap;
      next.set(pin.id, { ...existing, at, html, tappable });
      bag.delete(pin.id);
      continue;
    }
    // `preventMapInteractions` is a constructor option with no setter, so a pin
    // that gained or lost its card is rebuilt rather than mutated.
    existing?.marker.destroy();
    bag.delete(pin.id);
    const marker = new sdk.HtmlMarker(map, {
      coordinates: at,
      html,
      // The pill is 28×28 and its point is its centre, so the anchor is half of
      // it — an HtmlMarker positions its TOP-LEFT corner by default.
      anchor: [14, 14],
      preventMapInteractions: tappable,
      zIndex: 20,
    });
    const live: MarkerEntry['live'] = { pin, onTap };
    if (tappable) {
      // `HtmlMarker` is not an `Evented` — it has no `on()` — so the listener
      // goes on the element it renders. That is also why the tap is a DOM
      // click: it costs no map event and no run.
      const element = marker.getContent();
      element.style.cursor = 'pointer';
      element.addEventListener('click', (event) => {
        event.stopPropagation();
        live.onTap?.(live.pin);
      });
    }
    next.set(pin.id, { marker, at, html, tappable, live });
  }
  // Whatever is left in the old bag is a pin the board dropped.
  for (const entry of bag.values()) entry.marker.destroy();
  bag.clear();
  return next;
}

/** Destroys every marker in a bag — the card left the board. */
export function clearMarkers(bag: MarkerBag): MarkerBag {
  for (const entry of bag.values()) entry.marker.destroy();
  bag.clear();
  return bag;
}

export type RouteLine = { line: Polyline; key: string } | null;
export type RouteLines = { walked: RouteLine; ahead: RouteLine; at: number };

export const EMPTY_ROUTE_LINES: () => RouteLines = () => ({ walked: null, ahead: null, at: 0 });

/**
 * The identity of a drawn line: its geometry AND its colour.
 *
 * The colour is in the key because `--accent` is a different navy in the dark
 * theme, and `Polyline` has no `setColor` — a theme switch has to rebuild the
 * line or the walk stays the wrong colour until the next frame moves it.
 */
export function lineKey(points: readonly LngLat[], color: string, width: number): string {
  return `${color}|${width}|${points.map((p) => `${p[0].toFixed(6)},${p[1].toFixed(6)}`).join(';')}`;
}

/**
 * Whether a line whose key changed may be rebuilt YET.
 *
 * `Polyline` cannot be edited, so every change is a destroy plus a construct,
 * and in the navigation mode the cut point between «walked» and «ahead» moves
 * with every GPS fix. Unthrottled that is a rebuild per second for the whole
 * walk. Pure so the throttle is testable without a map.
 */
export function shouldRedraw(
  previousKey: string | null,
  nextKey: string,
  lastAt: number,
  now: number,
  minIntervalMs: number,
): boolean {
  if (previousKey === nextKey) return false;
  if (previousKey === null) return true;
  return now - lastAt >= minIntervalMs;
}

export type RouteStyle = { color: string; width: number; zIndex: number };

/** The walk still ahead: the accent, drawn over the part already behind. */
export function aheadStyle(): RouteStyle {
  return { color: cssToken('--accent', '#172651'), width: 8, zIndex: 12 };
}

/** The part already walked: the muted grey of the ramp. */
export function walkedStyle(): RouteStyle {
  return { color: cssToken('--ink-faint', '#6f6f6f'), width: 6, zIndex: 10 };
}

/**
 * Draws (or redraws) the two halves of the route.
 *
 * In preview mode `walked` is empty and only one line exists; in navigation
 * mode the cut moves and both do.
 */
export function syncRoute(
  map: MapGLMap,
  sdk: Pick<typeof MapGL, 'Polyline'>,
  lines: RouteLines,
  ahead: readonly LngLat[],
  walked: readonly LngLat[],
  options: { now?: number; minIntervalMs?: number } = {},
): RouteLines {
  const now = options.now ?? Date.now();
  const minIntervalMs = options.minIntervalMs ?? 0;
  let changed = false;

  const half = (current: RouteLine, points: readonly LngLat[], style: RouteStyle): RouteLine => {
    // A line needs two vertices; one is a point and MapGL draws nothing.
    if (points.length < 2) {
      if (current) {
        current.line.destroy();
        changed = true;
      }
      return null;
    }
    const key = lineKey(points, style.color, style.width);
    if (!shouldRedraw(current?.key ?? null, key, lines.at, now, minIntervalMs)) return current;
    current?.line.destroy();
    changed = true;
    const line = new sdk.Polyline(map, {
      coordinates: points.map((p) => [p[0], p[1]]),
      color: style.color,
      width: style.width,
      zIndex: style.zIndex,
    });
    return { line, key };
  };

  const nextWalked = half(lines.walked, walked, walkedStyle());
  const nextAhead = half(lines.ahead, ahead, aheadStyle());
  return { walked: nextWalked, ahead: nextAhead, at: changed ? now : lines.at };
}

/** Destroys both lines — the card left the board. */
export function clearRoute(lines: RouteLines): RouteLines {
  lines.walked?.line.destroy();
  lines.ahead?.line.destroy();
  return EMPTY_ROUTE_LINES();
}

/**
 * The dashed overlay: stretches of the walk that are not ordinary path.
 *
 * A SEPARATE set of lines above the route rather than a per-leg rebuild of it.
 * The base line is already cut in two by the visitor's position and rebuilt on
 * a throttle; slicing it a second time by style would multiply both, and a
 * stairway does not move when the visitor does. So these are drawn once per
 * route and left alone — `key` carries the leg's own identity, so a card update
 * that did not change the walk destroys nothing.
 */
export type DashedBag = Map<string, { line: Polyline; key: string }>;

export function stairwayStyle(): RouteStyle {
  return { color: cssToken('--accent', '#172651'), width: 5, zIndex: 14 };
}

export function syncDashed(
  map: MapGLMap,
  sdk: Pick<typeof MapGL, 'Polyline'>,
  bag: DashedBag,
  segments: readonly { key: string; points: readonly LngLat[] }[],
): DashedBag {
  const style = stairwayStyle();
  const next: DashedBag = new Map();
  for (const segment of segments) {
    if (segment.points.length < 2) continue;
    const key = lineKey(segment.points, style.color, style.width);
    const existing = bag.get(segment.key);
    if (existing && existing.key === key) {
      next.set(segment.key, existing);
      bag.delete(segment.key);
      continue;
    }
    existing?.line.destroy();
    bag.delete(segment.key);
    const line = new sdk.Polyline(map, {
      coordinates: segment.points.map((p) => [p[0], p[1]]),
      color: style.color,
      width: style.width,
      zIndex: style.zIndex,
      // Dashes are the whole message: 2GIS gives pedestrians no turn-by-turn,
      // so «here you climb» is said by the line rather than by an instruction.
      dashLength: 6,
      gapLength: 6,
    });
    next.set(segment.key, { line, key });
  }
  for (const entry of bag.values()) entry.line.destroy();
  bag.clear();
  return next;
}

/** Destroys every dashed overlay — the card left the board. */
export function clearDashed(bag: DashedBag): DashedBag {
  for (const entry of bag.values()) entry.line.destroy();
  bag.clear();
  return bag;
}
